import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, Button, Space, Alert, Progress, Row, Col, Statistic, Typography, message } from "antd";
import { SyncOutlined, ReloadOutlined, DownloadOutlined, ImportOutlined } from "@ant-design/icons";
import { toggleDebugFeatures, loadDebugSettings, selectDebugFeatures } from "../redux/slices/appSlice";
import { useTriggerSyncMutation, useGetSyncStatusQuery, useGetLastSyncQuery } from "../redux/api/zendeskSyncApi";
import { getErrorMessage } from "../redux/api/baseApi";
import { selectUser } from "../redux/slices/authSlice";
import axiosInstance from "../services/axiosInstance";

const { Text } = Typography;

const SettingsPage = () => {
  const dispatch = useDispatch();
  const debugFeatures = useSelector(selectDebugFeatures);
  const user = useSelector(selectUser);
  const [currentSyncId, setCurrentSyncId] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [splitPayload, setSplitPayload] = useState(null);
  const [splitPrepareLoading, setSplitPrepareLoading] = useState(false);
  
  const [personTypes] = useState([
    { id: 1, name: "Customer" },
    { id: 2, name: "Manufacturer" },
    { id: 3, name: "Contact Person" },
  ]);

  const [statuses] = useState([
    { id: 1, name: "Pending", category: "Order Status" },
    { id: 2, name: "Completed", category: "Order Status" },
    { id: 3, name: "Draft", category: "Proposal Status" },
  ]);

  // Sync everything mutation
  const [triggerSync, { isLoading: triggeringSync }] = useTriggerSyncMutation();

  // Get sync status (with polling if sync is running)
  const {
    data: syncStatusData,
    refetch: refetchSyncStatus,
  } = useGetSyncStatusQuery(currentSyncId, {
    pollingInterval: isSyncing ? 2000 : 0, // Poll every 2 seconds if syncing
    skip: !currentSyncId,
  });

  // Get last sync
  const {
    data: lastSyncData,
    refetch: refetchLastSync,
  } = useGetLastSyncQuery();

  const syncStatus = syncStatusData;
  const lastSync = lastSyncData?.last_sync;

  // Load debug settings on component mount
  useEffect(() => {
    dispatch(loadDebugSettings());
  }, [dispatch]);

  // Handle sync completion
  useEffect(() => {
    if (syncStatus?.status === 'completed' || syncStatus?.status === 'failed') {
      setIsSyncing(false);
      setCurrentSyncId(null);
      
      if (syncStatus?.status === 'completed') {
        message.success('Sync completed successfully');
        refetchLastSync();
      } else {
        message.error('Sync failed. Please check the errors.');
      }
    }
  }, [syncStatus?.status, refetchLastSync]);

  // Handle sync everything button
  const handleSyncEverything = async () => {
    try {
      const result = await triggerSync(['deals', 'contacts', 'tasks']).unwrap();
      
      if (result.status === 'already_running') {
        message.info('Sync is already in progress');
        setCurrentSyncId(result.sync_id);
        setIsSyncing(true);
        return;
      }
      
      if (result.success && result.sync_id) {
        setCurrentSyncId(result.sync_id);
        setIsSyncing(true);
        message.info('Sync started in background');
      }
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  };

  const isAdmin = user && Number(user.role_id) === 1;

  const handlePrepareDealSplit = async () => {
    try {
      setSplitPrepareLoading(true);
      const { data } = await axiosInstance.get(
        "zendesk-deals/export-open-split-three/prepare",
        { timeout: 300000 }
      );
      if (!data?.success || !Array.isArray(data.teams) || data.teams.length !== 3) {
        message.error(data?.error || "Unexpected response from server");
        return;
      }
      setSplitPayload({
        teams: data.teams,
        totals: data.totals,
        generated_at: data.generated_at,
      });
      message.success(
        `${data.totals?.open_deals ?? "?"} open deals split into three CSVs — ready to download`
      );
    } catch (error) {
      setSplitPayload(null);
      message.error(getErrorMessage(error) || "Failed to load deals from Zendesk");
    } finally {
      setSplitPrepareLoading(false);
    }
  };

  const handleDownloadPreparedTeam = (team) => {
    if (!team?.csv) return;
    const blob = new Blob([team.csv], { type: "text/csv;charset=UTF-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = team.filename || `zendesk-team-${team.slot}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Helper function to format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  return (
    <div>
      <h1 className="mb-4">Settings</h1>

      {/* Zendesk Sync Section */}
      <Card
        className="mb-4"
        title={
          <Space>
            <SyncOutlined />
            <span>Zendesk Synchronization</span>
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              refetchLastSync();
              refetchSyncStatus();
            }}
            size="small"
          >
            Refresh
          </Button>
        }
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Alert
            message="Automatic Sync"
            description="Zendesk data is automatically synced every 5 minutes in the background. Use the button below to trigger a manual sync if needed."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Button
            type="primary"
            size="large"
            icon={<SyncOutlined />}
            onClick={handleSyncEverything}
            loading={triggeringSync || isSyncing}
            disabled={isSyncing}
            block
          >
            {isSyncing ? 'Syncing...' : 'Sync Everything Now'}
          </Button>

          {/* Progress Display */}
          {isSyncing && syncStatus?.track_progress && (
            <div style={{ marginTop: 16 }}>
              <Progress
                percent={syncStatus.progress_percent || 0}
                status={syncStatus.status === 'failed' ? 'exception' : 'active'}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                {syncStatus.current_step || 'Starting sync...'}
              </Text>
              {syncStatus.duration_seconds && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Duration: {syncStatus.duration_seconds}s
                </Text>
              )}
            </div>
          )}

          {/* Sync Stats */}
          {syncStatus && syncStatus.stats && (
            <div style={{ marginTop: 16 }}>
              <Row gutter={16}>
                {syncStatus.stats.deals && (
                  <Col span={8}>
                    <Statistic
                      title="Deals"
                      value={syncStatus.stats.deals.fetched || 0}
                      suffix={`(${syncStatus.stats.deals.inserted || 0} new, ${syncStatus.stats.deals.updated || 0} updated)`}
                    />
                  </Col>
                )}
                {syncStatus.stats.contacts && (
                  <Col span={8}>
                    <Statistic
                      title="Contacts"
                      value={syncStatus.stats.contacts.fetched || 0}
                      suffix={`(${syncStatus.stats.contacts.inserted || 0} new, ${syncStatus.stats.contacts.updated || 0} updated)`}
                    />
                  </Col>
                )}
                {syncStatus.stats.tasks && (
                  <Col span={8}>
                    <Statistic
                      title="Tasks"
                      value={syncStatus.stats.tasks.fetched || 0}
                      suffix={`(${syncStatus.stats.tasks.inserted || 0} new, ${syncStatus.stats.tasks.updated || 0} updated)`}
                    />
                  </Col>
                )}
              </Row>
            </div>
          )}

          {/* Errors Display */}
          {syncStatus?.has_errors && syncStatus.errors && syncStatus.errors.length > 0 && (
            <Alert
              type="warning"
              message={`Sync completed with ${syncStatus.errors.length} error(s)`}
              style={{ marginTop: 16 }}
            />
          )}

          {/* Last Sync Info */}
          {lastSync && !isSyncing && (
            <Alert
              type="info"
              message="Last Sync"
              description={
                <div>
                  <p>
                    <strong>Completed:</strong>{' '}
                    {formatTimeAgo(lastSync.completed_at || lastSync.started_at)}
                  </p>
                  <p>
                    <strong>Duration:</strong> {lastSync.duration_seconds || 0}s
                  </p>
                  <p>
                    <strong>Started by:</strong> {lastSync.started_by === 'cron' ? 'Automatic' : 'Manual'}
                  </p>
                  {lastSync.has_errors && (
                    <p>
                      <Text type="warning">
                        <strong>Errors:</strong> {lastSync.errors?.length || 0}
                      </Text>
                    </p>
                  )}
                </div>
              }
              style={{ marginTop: 16 }}
            />
          )}
        </Space>
      </Card>

      {isAdmin ? (
        <Card
          className="mb-4"
          title={
            <Space>
              <DownloadOutlined />
              <span>Open Zendesk Sell deals — 3-way split</span>
            </Space>
          }
        >
          <Space orientation="vertical" style={{ width: "100%" }}>
            <Alert
              message="Admin export"
              description="Loads all open Sell deals once from Zendesk, splits them here on the server, then your browser keeps the three CSV payloads in memory until you dismiss or reload. Downloads are instant and do not call Zendesk again. Each row includes zendesk_sell_url (opens the deal in Zendesk Sell — same host as the deals list in the app). Split logic: active pipeline stages only; month from deal created_at (YYYY-MM or unknown); within each month, deals sorted by id; rows assigned to whoever has the fewest rows so team sizes stay balanced."
              type="warning"
              showIcon
            />

            <Button
              type="primary"
              size="large"
              icon={<ImportOutlined />}
              loading={splitPrepareLoading}
              onClick={handlePrepareDealSplit}
              block
            >
              {splitPrepareLoading
                ? "Loading from Zendesk & splitting…"
                : splitPayload
                  ? "Reload from Zendesk & re-split"
                  : "Load from Zendesk & split"}
            </Button>

            {splitPayload ? (
              <>
                <Alert
                  type="success"
                  message="Splits ready"
                  description={
                    <div>
                      <p>
                        <strong>Prepared:</strong>{" "}
                        {splitPayload.generated_at
                          ? new Date(splitPayload.generated_at).toLocaleString()
                          : "—"}
                      </p>
                      <p>
                        <strong>Open deals:</strong> {splitPayload.totals?.open_deals ?? "—"}
                      </p>
                      <p>
                        <strong>Rows per team:</strong>{" "}
                        {(splitPayload.totals?.counts || [])
                          .map((c, i) => `Team ${i + 1}: ${c}`)
                          .join(" · ")}
                      </p>
                    </div>
                  }
                  showIcon
                />
                <Space wrap style={{ width: "100%" }}>
                  {splitPayload.teams.map((team, idx) => {
                    const count = splitPayload.totals?.counts?.[idx];
                    const label =
                      count != null
                        ? `Download team ${team.slot} (${count} deals)`
                        : `Download team ${team.slot}`;
                    return (
                      <Button
                        key={team.slot ?? idx}
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadPreparedTeam(team)}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </Space>
                <Button type="link" onClick={() => setSplitPayload(null)} block style={{ padding: 0 }}>
                  Discard loaded splits
                </Button>
              </>
            ) : null}
          </Space>
        </Card>
      ) : null}

      {/* Developer Options */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Developer Options</h5>
          <small className="text-muted">Advanced features for development and debugging</small>
        </div>
        <div className="card-body">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="debugFeaturesToggle"
              checked={debugFeatures.enabled}
              onChange={() => dispatch(toggleDebugFeatures())}
            />
            <label className="form-check-label" htmlFor="debugFeaturesToggle">
              <strong>Enable Debug Features</strong>
              <div className="form-text">
                Shows PDF preview and other developer tools in proposal details
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Person Types */}
      <div className="card mb-4">
        <div className="card-header">Person Types</div>
        <div className="card-body">
          <button className="btn btn-primary mb-3">Add Person Type</button>
          <ul className="list-group">
            {personTypes.map((type) => (
              <li
                key={type.id}
                className="list-group-item d-flex justify-content-between"
              >
                {type.name}
                <button className="btn btn-sm btn-danger">Delete</button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Statuses */}
      <div className="card">
        <div className="card-header">Statuses</div>
        <div className="card-body">
          <button className="btn btn-primary mb-3">Add Status</button>
          <ul className="list-group">
            {statuses.map((status) => (
              <li
                key={status.id}
                className="list-group-item d-flex justify-content-between"
              >
                {status.name}{" "}
                <span className="badge bg-secondary">{status.category}</span>
                <button className="btn btn-sm btn-danger">Delete</button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
